import * as yup from "yup";
import { Formik, Form } from "formik";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useForgotPasswordMutation } from "../../../features/api/Api";
import Swal from "sweetalert2";
import { email_regex } from "../../../utils/regex";
import {
  Container,
  Grid,
  Typography,
  Button,
  Menu,
  MenuItem,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import RecaptchaInput from "../../../components/inputs/RecaptchaInput";

const languages = [
  {
    code: "tr",
    name: "Türkçe",
    country_code: "tr",
  },
  {
    code: "en",
    name: "English",
    country_code: "gb",
  },
];

export const ForgotPassword: FC = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [t, i18next] = useTranslation();
  const history = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (code: any) => {
    setAnchorEl(null);
    i18next.changeLanguage(code);
  };

  const validationSchema = yup.object({
    //recaptcha: yup.string().required("Zorunlu alanı doldurun."),
    customerCode: yup
      .string()
      .matches(/^[0-9]+$/, "Sadece rakam giriniz.")
      .required(t("Lütfen müşteri kodu giriniz."))
      .nullable(),
    email: yup
      .string()
      .email(t("resources.users.forgot_password.valid.valid_email"))
      .required(t("resources.users.forgot_password.valid.required_email"))
      .test(
        t("resources.users.forgot_password.valid.not_valid_email"),
        t("resources.users.forgot_password.valid.not_valid_email"),
        function () {
          return email_regex.test(this.parent.email) ? false : true;
        }
      ),
  });

  const handleSubmit = (values: any) => {
    forgotPassword(values)
      .unwrap()
      .then((data: any) => {
        Swal.fire({
          icon: "success",
          title: t("actions.successfully"),
          text: t("actions.send_email_success"),
        });
        history(
          `/reset-password?email=${values.email}&customerCode=${values.customerCode}`
        );
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Değerli Müşterimiz,",
          text: t("actions.something_went_wrong"),
        });
      });
  };
  return (
    <div className={`${process.env.REACT_APP_AUTH_CLASSNAME}`}>
      <Container>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "100vh" }}
        >
          <Grid item md={5}>
            <div className="card card-login">
              {!isLoading ? (
                <>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <div>
                      <img src="/img/logo.svg" alt="Fixity" />
                    </div>
                    <div>
                      <Typography variant="h5" align="right">
                        {t("resources.users.forgot_password.root")}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <div className="dropdown dropdown-language">
                      <Button
                        id="language-menu"
                        aria-controls="language-menu"
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        <div className="icon">
                          <i className="icon-world" />
                        </div>
                        <div className="body">
                          <p>{t("resources.users.forgot_password.language")}</p>
                        </div>
                      </Button>
                      <Menu
                        id="demo-positioned-menu"
                        aria-labelledby="demo-positioned-button"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        {languages.map((lang, index) => (
                          <MenuItem
                            key={index}
                            onClick={() => handleClose(lang.code)}
                          >
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>
                  </Grid>
                  <Formik
                    initialValues={{
                      email: "",
                      recaptcha: "",
                      customerCode: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      isSubmitting,
                    }) => (
                      <Form>
                        <>
                          <TextField
                            fullWidth
                            id="customerCode"
                            name="customerCode"
                            label={t("Bayi Kodu")}
                            value={values.customerCode}
                            onChange={handleChange}
                            error={
                              touched.customerCode &&
                              Boolean(errors.customerCode)
                            }
                            helperText={
                              touched.customerCode && errors.customerCode
                            }
                            style={{ marginBottom: "15px" }}
                          />
                          <TextField
                            fullWidth
                            id="email"
                            name="email"
                            label={t("resources.users.forgot_password.email")}
                            value={values.email}
                            onChange={handleChange}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            style={{ marginBottom: "15px" }}
                          />
                          <RecaptchaInput name="recaptcha" />
                          <Box sx={{ mx: "auto", width: 200 }}>
                            <Button
                              className="btn btn-default"
                              type="submit"
                              sx={{ mx: "auto", width: 200 }}
                            >
                              {t(
                                "resources.users.forgot_password.forgot_password_button"
                              )}
                            </Button>
                          </Box>
                          <Box sx={{ mx: "auto", width: 200 }}>
                            <p style={{ marginTop: "30px" }}>
                              {t(
                                "resources.users.forgot_password.already_registered"
                              )}
                              <Link to="/login">
                                {" "}
                                {t("resources.users.forgot_password.login")}
                              </Link>
                            </p>
                          </Box>
                        </>
                      </Form>
                    )}
                  </Formik>
                </>
              ) : (
                <CircularProgress />
              )}
            </div>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default ForgotPassword;
